import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Smartphone, Download, Apple, Play } from "lucide-react";

// Set REACT_APP_PLAY_STORE_URL once the Google Play listing is live.
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";

export const GetAppSection = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <section className="relative py-16 lg:py-20 border-y border-crystal/20 bg-charcoal/40" data-testid="get-app-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden bg-charcoal border border-crystal/25 rounded-2xl p-8 sm:p-12 text-center">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-venom/10 blur-3xl pointer-events-none" aria-hidden="true" />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4 flex items-center justify-center gap-2">
              <Smartphone size={14} /> The Label In Your Pocket
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
              Get the DGE app.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-ice/60 leading-relaxed max-w-lg mx-auto">
              Beats, merch, booking and the roster — one tap from your home screen.
              Free, instant, no app-store detour needed.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              {installPrompt && !installed && (
                <button
                  onClick={install}
                  className="inline-flex items-center gap-2.5 bg-venom text-obsidian font-bold text-sm px-7 py-4 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 transition-all duration-300"
                  data-testid="app-install-button"
                >
                  <Download size={17} /> Install Now — Free
                </button>
              )}

              {PLAY_STORE_URL ? (
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white text-obsidian font-semibold text-sm px-6 py-3.5 rounded-xl hover:scale-[1.03] active:scale-95 transition-transform duration-300"
                  data-testid="google-play-badge"
                >
                  <Play size={20} fill="currentColor" />
                  <span className="text-left leading-tight">
                    <span className="block text-[9px] uppercase tracking-wider opacity-70">Get it on</span>
                    <span className="block text-base font-bold">Google Play</span>
                  </span>
                </a>
              ) : (
                <span
                  className="inline-flex items-center gap-3 bg-white/8 border border-white/15 text-ice/60 font-semibold text-sm px-6 py-3.5 rounded-xl cursor-default"
                  data-testid="google-play-badge-soon"
                  title="Google Play listing in progress"
                >
                  <Play size={20} />
                  <span className="text-left leading-tight">
                    <span className="block text-[9px] uppercase tracking-wider opacity-70">Coming soon on</span>
                    <span className="block text-base font-bold">Google Play</span>
                  </span>
                </span>
              )}

              <span
                className="inline-flex items-center gap-3 bg-white/8 border border-white/15 text-ice/60 font-semibold text-sm px-6 py-3.5 rounded-xl cursor-default"
                data-testid="app-store-badge-soon"
                title="App Store listing in progress"
              >
                <Apple size={20} />
                <span className="text-left leading-tight">
                  <span className="block text-[9px] uppercase tracking-wider opacity-70">Coming soon on the</span>
                  <span className="block text-base font-bold">App Store</span>
                </span>
              </span>
            </div>

            <p className="mt-6 text-[11px] text-ice/40 leading-relaxed max-w-md mx-auto">
              Android: tap Install above (or browser menu → &ldquo;Add to Home screen&rdquo;).
              iPhone: Share → &ldquo;Add to Home Screen&rdquo;. Native store listings roll out next.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
